"use client";
import React, { useState, useRef, useId, KeyboardEvent } from "react";
import type { TooltipProps } from "../types";

// Add styles for tooltip content to ensure proper wrapping
const TooltipContentWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-w-[300px] whitespace-normal break-words p-2">
      {children}
    </div>
  );
};

export default function Default({
  content,
  children,
  className = "",
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipId = useId();

  const showTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const hideTooltip = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 130);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Escape" || event.key === "Esc") {
      setIsVisible(false);
    }
  };

  return (
    <span
      className={`relative inline-block ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
      {/* tooltip */}
      {isVisible && (
        <span
          id={tooltipId}
          role="tooltip"
          className={`
            absolute left-1/2 -top-2 -translate-x-1/2 -translate-y-full z-20
            whitespace-normal break-words rounded bg-white px-1 py-2 text-sm text-gray-800 shadow-lg
            max-w-[300px] z-50
          `}
        >
          <TooltipContentWrapper>{content}</TooltipContentWrapper>
          {/* arrow */}
          <span
            className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2
              rotate-45 bg-white shadow-lg"
          />
        </span>
      )}
    </span>
  );
}
